import { Injectable } from '@angular/core';
import { Credentials, IAuthStrategy, UsernameAndPassword } from '../interfaces/auth-strategy.interface';
import { HttpClient } from '@angular/common/http';
import {forkJoin, map, Observable, of, throwError} from 'rxjs';
import {catchError, tap} from 'rxjs/operators';
import {WorkspaceNewService} from '../../services/workspace-new.service';
import {TokenService} from '../../services/webFeeder/token.service';
import {HttpService} from '../../services/http-service';
import {Login} from '../../model/login.model';
import {Constants} from '../../utils/constants';
import {environment} from '../../../environments/environment';
import {OmsAdmin} from '../../model/recover-password';
import {ChangePassword} from '../../model/change-password';
import {RecoverTypeEnum} from '../../model/enum/recover-type.enum';
import {ConfigService} from '../../services/webFeeder/config.service';

@Injectable({
  providedIn: 'root'
})
export class SaasAuthStrategyService implements IAuthStrategy {

  constructor(
    private httpsvc: HttpClient,
    private httpService: HttpService,
    private tokenService: TokenService,
    private workspaceService: WorkspaceNewService,
    private configService: ConfigService
  ) { }

  login(credentials: Credentials<UsernameAndPassword>): Observable<Object> {
    const { username, password } = credentials.data;
    const credential: Login = {
      username: username,
      password: password,
      softwareVersion: environment.version,
      softwareName: Constants.DEFAULT_SOFTWARE_NAME + '_' + environment.BROKER
    };
    const user = { username: credential.username, firstName: credential.username, lastName: credential.username };
    this.setCurrentUser(user);
    const params = JSON.stringify(credential);
    return this.httpsvc.post(this.configService.getApiWebFeederRest() + '/services/AuthFast/LoginSignIn', params, Constants.httpOptions).pipe(
      tap((result: any) => {
        this.setAccountNumber(result.accountNumber);
        this.setTokenWs(result.tokenWS);
        const webfeederUrl: string = environment.WEB_FEEDER_BASE_WOS_URL + '?reconnect=' + this.getTokenWs();
        this.tokenService.assignServices(webfeederUrl);
      })
    );
  }

  logout(): boolean | Observable<Object> {
    const token = this.getTokenApi();
    const username = this.getBrokerAccount();
    if (token && username) {
      const credential: any = { username: username };
      const params = JSON.stringify(credential);
      return forkJoin(this.httpsvc.post(this.configService.getApiWebFeederRest() + '/services/Authenticate/LogoutHomeBroker', params, Constants.httpOptions),
        this.tokenService.revokeAPIGatewayToken(token),
        this.workspaceService.revokeAPIWorkspacesToken(token)).pipe(
          map(([resp0,]) => {
            return resp0;
          }));
    } else if (token) {
      this.tokenService.revokeAPIGatewayToken(token).subscribe();
      this.workspaceService.revokeAPIWorkspacesToken(token)
        .subscribe(() => {
          return true;
        });
      return true;
    }
    return false;
  }

  recoverPassword(email: string, type: number) {
    const credential: OmsAdmin = {
      broker: this.configService.getBroker(),
      email: email,
      type: type ? type : 2
    };
    return this.httpsvc.post(this.configService.getApiWebFeederRest() + '/services/AuthFast/RecoverPassword',
      credential, Constants.httpOptions)
      .pipe(map(res => {
        return res;
      }))
  }

  difusionChangePassword(login: string, oldPassword: string, newPassword: string, isPasswordExpired = false, recoverType?) {
    if (!login) {
      return of([]);
    }
    if (recoverType === RecoverTypeEnum.NEGOTIATION) {
      const credential: ChangePassword = {
        username: login,
        password: oldPassword,
        newPassword: newPassword,
        clientDocument: '',
        softwareName: '',
        softwareVersion: '',
        ipAdress: ''
      };
      return this.httpsvc
        .post(this.configService.getApiWebFeederRest() + '/services/Authenticate/ChangePasswordHomeBroker', credential)
        .pipe(map(response => {
          if (response) {
            return response;
          }
          return [];
        }));
    } else {
      if (!isPasswordExpired) {
        const credential: OmsAdmin = {
          login: login,
          oldPassword: oldPassword,
          newPassword: newPassword
        };
        return this.httpsvc
          .post(this.configService.getApiWebFeederRest() + '/services/AuthFast/difusionChangePassword', credential)
          .pipe(
            map(res => {
              return res;
            }),
            catchError((error: any) => [throwError(() => error)])
          )
        // .catch((error: any) => [Observable.throw(error)]);
      } else {
        const credential: ChangePassword = {
          username: login,
          password: oldPassword,
          newPassword: newPassword,
          clientDocument: '',
          softwareName: '',
          softwareVersion: '',
          ipAdress: ''
        };
        return this.httpsvc
          .post(this.configService.getApiWebFeederRest() + '/services/Authenticate/ChangePasswordHomeBroker', credential)
          .pipe(map(response => {
            if (response) {
              return response;
            }
            return [];
          }));
      }
    }
  }

  refresh(): Observable<any> {
    const params: any = {};
    params.refresh_token = this.getRefreshToken();
    return this.httpsvc.post(this.configService.getApiWebFeederRest() + '/services/AuthFast/RefreshToken', params, Constants.httpOptions);
  }

  getCurrentUser() {
    const userLogged: string = window.sessionStorage.getItem(Constants.COOKIE_HB_CURRENTUSER);
    return userLogged ? JSON.parse(userLogged) : null;
  }

  getBrokerAccount() {
    return window.sessionStorage.getItem(Constants.COOKIE_HB_BROKER_ACCOUNT);
  }

  setCurrentUser(user) {
    const json: string = typeof (user) === 'string' ? user : JSON.stringify(user);
    window.sessionStorage.setItem(Constants.COOKIE_HB_CURRENTUSER, json);
  }

  getTokenApi(): string {
    return window.sessionStorage.getItem(Constants.COOKIE_TOKEN_WEBAPI);
  }

  getTokenWs(): string {
    return window.sessionStorage.getItem(Constants.COOKIE_TOKEN_WEBFEEDER);
  }

  getRefreshToken(): string {
    return window.sessionStorage.getItem(Constants.COOKIE_TOKEN_REFRESH_WEBAPI);
  }

  setTokenApi(value: string) {
    return window.sessionStorage.setItem(Constants.COOKIE_TOKEN_WEBAPI, value);
  }

  setTokenWs(value: string) {
    return window.sessionStorage.setItem(Constants.COOKIE_TOKEN_WEBFEEDER, value);
  }

  setAccountNumber(account: string) {
    window.sessionStorage.setItem(Constants.COOKIE_HB_ACCOUNT, account);
  }

  // brokerServiceLogin(username: string, password: string, broker: string) {
  //   let url = 'services/Authenticate/LoginHomeBroker';
  //   if (this.configService.isAuthenticationBroker()) {
  //     url = 'services/AuthFast/LoginSignIn';
  //   }
  //   const credential: Login = {
  //     username: username,
  //     password: password,
  //     softwareVersion: environment.version,
  //     softwareName: Constants.DEFAULT_SOFTWARE_NAME + '_' + environment.BROKER
  //   };
  //   const params = JSON.stringify(credential);
  //   return this.httpService
  //     .doPost(this.configService.getApiWebFeederRest(), url, params);
  // }
  //
  // loginWS(guidUser?: string) {
  //   return this.httpService
  //     .doGet(this.configService.getApiWebFeederRest(), 'services/AuthFast/LoginWS?guidUser=' + guidUser)
  //     .pipe(map(response => {
  //       if (response) {
  //         const json = response;
  //         return json;
  //       }
  //       return [];
  //     }));
  // }
}
